/**
 * The wire contract between the tracker's hooks and its dispatcher.
 *
 * A pure module with NO imports, deliberately. The hooks reach `lib/firebase`
 * at module load, so anything importing one drags a live client into the test
 * runner and fails with auth/invalid-api-key. That is the third time this
 * hazard has appeared in this feature, after the taxonomy barrel and the
 * access rules, so the constants live where they can actually be asserted.
 *
 * These strings are the Zod discriminated-union literals on the server. A
 * mismatch is not a type error on either side: it is a runtime
 * "invalid-argument" from a callable that looks correct in both files.
 */

export const REPORT_CALLABLE = 'pipelineTrackerReportAction';

export const PIPELINE_REPORT_ACTION = 'pipelineReport';
export const TASKS_REPORT_ACTION = 'tasksReport';

export const INGESTION_CALLABLE = 'pipelineTrackerIngestionAction';

export const SUBMIT_SNAPSHOT_ACTION = 'submitSnapshot';
export const LIST_UPLOADS_ACTION = 'listUploads';

/** Report types the ingestion callable accepts today. */
export const UPLOADABLE_REPORT_TYPES = ['performance', 'tasks', 'sales', 'compliance'] as const;
export type UploadableReportType = (typeof UPLOADABLE_REPORT_TYPES)[number];

/**
 * Per-report limits, mirrored from the server registry.
 *
 * Duplicated deliberately: the browser bundle cannot reach the functions
 * package, and the isolation fence forbids a shared one. Checking here means
 * the operator is told before a large upload starts rather than after it fails.
 */
export const REPORT_LIMITS: Record<UploadableReportType, { maxBytes: number; maxRows: number; label: string }> = {
  performance: { maxBytes: 6 * 1024 * 1024, maxRows: 12_000, label: 'Performance' },
  tasks: { maxBytes: 8 * 1024 * 1024, maxRows: 40_000, label: 'Tasks' },
  // Sales is the widest export at 105 columns, hence the larger byte ceiling on
  // the same row count. Both mirror REPORT_TYPES in reportTypes.ts exactly; a
  // number that drifts here refuses a file the server would have accepted.
  sales: { maxBytes: 12 * 1024 * 1024, maxRows: 12_000, label: 'Sales' },
  compliance: { maxBytes: 6 * 1024 * 1024, maxRows: 12_000, label: 'Compliance' },
};

/** Which tab each upload feeds. Shown at the point of choosing, because none of
 *  these report names is the name of a tab and the operator should not have to
 *  guess which screen moved. */
export const REPORT_DESTINATION: Record<UploadableReportType, string> = {
  performance: 'Pipeline and Trends',
  tasks: 'Tasks',
  sales: 'Lead source',
  compliance: 'Compliance',
};

/**
 * Base64 inflates by 4/3, and the server checks the ENCODED length too.
 *
 * A file can sit under the byte limit and still be refused once encoded, so the
 * client applies the same ceiling and explains it before sending rather than
 * letting the operator wait for a rejection.
 */
export const encodedCeilingFor = (reportType: UploadableReportType): number =>
  Math.ceil((REPORT_LIMITS[reportType].maxBytes * 4) / 3) + 1024;

export const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'] as const;

export const hasAcceptedExtension = (fileName: string): boolean =>
  ACCEPTED_EXTENSIONS.some((ext) => fileName.toLowerCase().endsWith(ext));

/**
 * What each refusal means, in the operator's terms.
 *
 * The server sends a stable code and, where it helps, the header names. It
 * deliberately never sends a cell value, so nothing here can render one. Codes
 * are matched exactly rather than by substring: a rewording of the server's
 * sentence must not change what the operator is told to do.
 */
export const REFUSAL_GUIDANCE: Record<string, { title: string; guidance: string; showsHeaders: boolean }> = {
  missing_required_headers: {
    title: 'That export is missing columns this report needs',
    guidance: 'Re-run the export with every column included. A partial export cannot be told apart from a different report.',
    showsHeaders: true,
  },
  duplicate_headers: {
    title: 'That export has the same column twice',
    guidance: 'Re-run the export without editing it. A duplicated column makes the values ambiguous.',
    showsHeaders: true,
  },
  unknown_headers: {
    title: 'That export has columns we do not recognise',
    guidance: 'Salestrekker may have added columns. Send this list to support so the new columns can be reviewed before anything is stored.',
    showsHeaders: true,
  },
  forbidden_headers: {
    title: 'That export contains data we will not store',
    guidance: 'The whole file was refused and nothing was saved. Re-run the export without these columns.',
    showsHeaders: true,
  },
  wrong_report_type: {
    title: 'That looks like a different report',
    guidance: 'Check the report selected above matches the export you chose, then upload again.',
    showsHeaders: false,
  },
  no_matching_sheet: {
    title: 'No sheet in that workbook has the expected columns',
    guidance: 'Upload the workbook as exported. The data sheet is found automatically, so there is no need to split it up.',
    showsHeaders: true,
  },
  ambiguous_matching_sheets: {
    title: 'More than one sheet could be the data sheet',
    guidance: 'This workbook has several sheets with the same columns, so the data sheet cannot be identified. Export it again without the extra copies.',
    showsHeaders: false,
  },
};

/**
 * The earliest date a custom reporting window may start on, mirrored from periods.ts.
 *
 * The server refuses anything below it, so without the same floor here a mistyped year reaches
 * the callable as a perfectly valid date, comes back refused, and the operator is left looking
 * at a refusal instead of a date picker that never offered the date. Duplicated for the same
 * reason REPORT_LIMITS is: the browser bundle cannot reach the functions package, and the
 * tracker isolation fence forbids a shared one.
 */
export const EARLIEST_WINDOW_START = '2000-01-01';

export const LEAD_SOURCE_REPORT_ACTION = 'leadSourceReport';
export const COMPLIANCE_REPORT_ACTION = 'complianceReport';
export const TRENDS_REPORT_ACTION = 'trendsReport';
/** Marketing tab: tenant-entered ad spend over leads created. */
export const MARKETING_REPORT_ACTION = 'marketingReport';

/**
 * The taxonomy dispatcher: mapping a raw provider stage to a canonical one.
 *
 * `saveMappings` takes the WHOLE array, not a delta, and carries the `version`
 * the client loaded. That is read-modify-write with optimistic concurrency: two
 * admins editing at once, the second save is refused with `aborted` rather than
 * quietly overwriting the first. A delta API would have hidden that conflict.
 */
export const TAXONOMY_CALLABLE = 'pipelineTrackerTaxonomyAction';

export const GET_TAXONOMY_ACTION = 'getTaxonomy';
export const SAVE_MAPPINGS_ACTION = 'saveMappings';
/** Broker roster. Same callable: the tracker's settings surface is one dispatcher. */
export const GET_ROSTER_ACTION = 'getRoster';
export const SAVE_ROSTER_ACTION = 'saveRoster';
export const LIST_UNMAPPED_ACTION = 'listUnmapped';
/** Monthly ad spend. Same callable, same gate and audit as the roster. */
export const GET_AD_SPEND_ACTION = 'getAdSpend';
export const SAVE_AD_SPEND_ACTION = 'saveAdSpend';

/** Mirrors TAXONOMY_TYPES on the server. Only deal stages have a UI today. */
export const DEAL_STAGE_TAXONOMY = 'deal_stage';

/**
 * Roles allowed to edit a mapping, mirrored from TAXONOMY_ROLES on the server.
 *
 * The server is the gate; this list only decides whether the controls RENDER.
 * A mapping edit changes every figure the whole company sees, which is why
 * office_admin, the broker family and marketing are all excluded rather than
 * merely being unlikely to use it.
 */
export const TAXONOMY_EDITOR_ROLES = ['super_admin', 'administrator'] as const;

export const canEditTaxonomy = (role: string | undefined): boolean =>
  TAXONOMY_EDITOR_ROLES.includes(role as (typeof TAXONOMY_EDITOR_ROLES)[number]);

/** A mapping row applying to every workflow. Mirrors WORKFLOW_WILDCARD. */
export const WORKFLOW_WILDCARD = '*';
